/**
 * Festejo (Esteban, 2026-10-13: el marcador tiene que sentirse "gamy"): confeti + un arpegio corto tipo videojuego al
 * guardar una Promesa con fecha y al Matriculado. Sin dependencias: un <canvas> encima de todo que no estorba los clics.
 * Con "reducir movimiento" del sistema solo sale el aviso. Se apaga con "silenciar" (localStorage wapp_festejo = "off").
 */
import "./formCita.css";

export type TipoFestejo = "promesa" | "matricula";

const LLAVE = "wapp_festejo";
const apagado = (): boolean => { try { return localStorage.getItem(LLAVE) === "off"; } catch { return false; } };
const silenciar = () => { try { localStorage.setItem(LLAVE, "off"); } catch { /* sin almacenamiento */ } };

/** Toma el color real del token (claro u oscuro); si no hay, el de siempre. */
const token = (n: string, def: string): string => {
	try { return getComputedStyle(document.documentElement).getPropertyValue(n).trim() || def; } catch { return def; }
};

const confeti = (tipo: TipoFestejo) => {
	const c = document.createElement("canvas");
	const dpr = Math.min(2, window.devicePixelRatio || 1);
	const W = window.innerWidth, H = window.innerHeight;
	c.width = W * dpr; c.height = H * dpr;
	c.className = "festejo-lienzo";
	c.setAttribute("aria-hidden", "true");
	c.style.cssText = "position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:3000";
	document.body.appendChild(c);
	const ctx = c.getContext("2d");
	if (!ctx) { c.remove(); return; }
	ctx.scale(dpr, dpr);
	const colores = [token("--wa-acento", "#F45030"), token("--wa-oro", "#A8730A"), token("--wa-azul", "#2B62E0"), "#FFD08A"];
	if (tipo === "matricula") colores.push(token("--wa-verde", "#1F7A54"), token("--wa-verde", "#1F7A54"));
	// Dos cañones desde las esquinas de abajo, hacia el centro.
	const N = 120;
	const ps = Array.from({ length: N }, (_, i) => {
		const izq = i % 2 === 0;
		const ang = (izq ? -60 : -120) * Math.PI / 180 + (Math.random() - 0.5) * 0.7;
		const vel = 9 + Math.random() * 8;
		return {
			x: izq ? W * 0.08 : W * 0.92, y: H * 0.95, vx: Math.cos(ang) * vel, vy: Math.sin(ang) * vel * 1.25,
			w: 6 + Math.random() * 6, h: 3 + Math.random() * 4, r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.4,
			col: colores[i % colores.length],
		};
	});
	const DUR = 1800;
	const t0 = performance.now();
	const cuadro = (t: number) => {
		const e = t - t0;
		ctx.clearRect(0, 0, W, H);
		const alfa = e > DUR - 400 ? Math.max(0, (DUR - e) / 400) : 1;
		for (const p of ps) {
			p.vy += 0.32; p.vx *= 0.99; p.x += p.vx; p.y += p.vy; p.r += p.vr;
			ctx.save();
			ctx.globalAlpha = alfa;
			ctx.translate(p.x, p.y); ctx.rotate(p.r);
			ctx.fillStyle = p.col;
			ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h * Math.abs(Math.cos(p.r * 2)) + 1);
			ctx.restore();
		}
		if (e < DUR) requestAnimationFrame(cuadro); else c.remove();
	};
	requestAnimationFrame(cuadro);
};

/** Arpegio de 3 notas (matrícula: una cuarta más alta al final). Onda cuadrada suave, volumen 0.25, ~0.5 s. */
const sonido = (tipo: TipoFestejo) => {
	try {
		const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!AC) return;
		const ac = new AC();
		const notas = tipo === "matricula" ? [523.25, 659.25, 783.99, 1046.5] : [523.25, 659.25, 783.99];
		const paso = 0.11;
		notas.forEach((f, i) => {
			const o = ac.createOscillator(), g = ac.createGain();
			o.type = "square"; o.frequency.value = f;
			const t = ac.currentTime + i * paso;
			const largo = i === notas.length - 1 ? 0.22 : paso;
			g.gain.setValueAtTime(0, t);
			g.gain.linearRampToValueAtTime(0.25 * 0.35, t + 0.01);   // la cuadrada suena fuerte: se baja para que dé ~0.25 percibido
			g.gain.exponentialRampToValueAtTime(0.001, t + largo);
			o.connect(g); g.connect(ac.destination);
			o.start(t); o.stop(t + largo + 0.02);
		});
		window.setTimeout(() => { try { Promise.resolve(ac.close()).catch(() => undefined); } catch { /* ya cerrado */ } }, 1200);
	} catch { /* sin audio */ }
};

/** Aviso abajo al centro con el enlace chiquito "silenciar". */
const aviso = (tipo: TipoFestejo) => {
	const d = document.createElement("div");
	d.className = "festejo-toast" + (tipo === "matricula" ? " matricula" : "");
	d.setAttribute("role", "status");
	const t = document.createElement("b");
	t.textContent = tipo === "matricula" ? "¡Matriculado!" : "¡Promesa con fecha!";
	const s = document.createElement("button");
	s.type = "button";
	s.textContent = "silenciar";
	s.onclick = () => { silenciar(); d.remove(); };
	d.appendChild(t); d.appendChild(s);
	document.body.appendChild(d);
	window.setTimeout(() => d.remove(), 3200);
};

export const festejo = (tipo: TipoFestejo) => {
	if (apagado()) return;
	aviso(tipo);
	const quieto = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	if (quieto) return;
	confeti(tipo);
	sonido(tipo);
};
